<template>
  <div
    class="us-state-map"
    @mousemove="onMouseMove"
  >
    <svg
      viewBox="0 0 960 620"
      xmlns="http://www.w3.org/2000/svg"
      @mouseleave="onSvgMouseLeave"
    >
      <!-- Main map states -->
      <g id="main-map">
        <path
          v-for="state in mainStates"
          :key="state.code"
          :d="state.path"
          :fill="getStateColor(state.code)"
          :stroke="getStateStroke(state.code)"
          stroke-width="1"
          class="state-path"
          @click.stop="handleStateClick(state.code)"
          @mouseenter="handleStateHover(state.code)"
          @mouseleave="hoveredState = null"
        />
        <!-- State labels -->
        <text
          v-for="state in mainStates"
          :key="`${state.code}-label`"
          :x="state.labelX"
          :y="state.labelY"
          text-anchor="middle"
          dominant-baseline="middle"
          font-size="10"
          font-weight="bold"
          fill="#333"
          pointer-events="none"
          class="state-label"
        >
          {{ state.code }}
        </text>
      </g>

      <!-- Inset boxes for AK, HI, territories -->
      <g id="inset-states">
        <g
          v-for="state in insetStatesWithPath"
          :key="state.code"
        >
          <!-- Render actual path for AK and HI -->
          <path
            :d="state.path"
            :fill="getStateColor(state.code)"
            :stroke="getStateStroke(state.code)"
            stroke-width="1"
            class="state-path"
            @click.stop="handleStateClick(state.code)"
            @mouseenter="handleStateHover(state.code)"
            @mouseleave="hoveredState = null"
          />
          <!-- Label for AK/HI -->
          <text
            :x="state.labelX"
            :y="state.labelY"
            text-anchor="middle"
            dominant-baseline="middle"
            font-size="10"
            font-weight="bold"
            fill="#333"
            pointer-events="none"
            class="state-label"
          >
            {{ state.code }}
          </text>
        </g>
        <!-- Box territories without paths -->
        <g
          v-for="state in visibleInsetStatesWithoutPath"
          :key="state.code"
          :transform="`translate(${state.insetX}, ${state.insetY})`"
        >
          <rect
            :width="insetBoxSize"
            :height="insetBoxSize"
            fill="#f3f4f6"
            stroke="#d1d5db"
            stroke-width="1"
          />
          <rect
            :width="insetBoxSize - 4"
            :height="insetBoxSize - 4"
            x="2"
            y="2"
            :fill="getStateColor(state.code)"
            :stroke="getStateStroke(state.code)"
            stroke-width="1"
            class="state-path"
            @click.stop="handleStateClick(state.code)"
            @mouseenter="handleStateHover(state.code)"
            @mouseleave="hoveredState = null"
          />
          <text
            :x="insetBoxSize / 2"
            :y="insetBoxSize + 12"
            text-anchor="middle"
            font-size="10"
            font-weight="bold"
            fill="#333"
          >
            {{ state.code }}
          </text>
        </g>
      </g>
    </svg>

    <!-- Hover tooltip (non-insights mode) -->
    <div
      v-if="hoveredState && !useClickPopup"
      class="map-tooltip"
    >
      <div class="tooltip-header">
        <strong>{{ getStateName(hoveredState) }}</strong>
      </div>
      <div>{{ getStateMarginText(hoveredState) }}</div>
    </div>

    <!-- Hover/pinned popup (insights mode) -->
    <div
      v-if="popupState && useClickPopup"
      :class="['map-popup', { 'map-popup--pinned': isPinned }]"
      :style="popupStyle"
      @click.stop
    >
      <div class="popup-header">
        <div class="popup-title">
          <strong>{{ getStateName(popupState) }}</strong>
          <template v-if="selectedDayLabel">
            <span class="tooltip-separator" />
            <span class="tooltip-day">{{ selectedDayLabel }}</span>
            <template v-if="selectedTimeLabel">
              <span class="tooltip-time">{{ selectedTimeLabel }}</span>
            </template>
          </template>
          <template v-if="stateCampaignDetails[popupState]">
            <span class="tooltip-separator" />
            <span class="tooltip-count">{{ stateCampaignDetails[popupState].length }} {{ stateCampaignDetails[popupState].length === 1 ? 'campaign' : 'campaigns' }}</span>
          </template>
        </div>
        <button
          v-if="isPinned"
          type="button"
          class="popup-close"
          @click="closePopup"
        >
          &times;
        </button>
      </div>
      <div class="popup-body">
        <template v-if="stateCampaignDetails[popupState]">
          <div
            v-for="(item, idx) in stateCampaignDetails[popupState]"
            :key="idx"
            class="campaign-line"
          >
            <span class="campaign-meta">
              <router-link
                v-if="item.campaignId"
                :to="{ name: 'BuyerCampaignDetails', params: { id: item.campaignId } }"
                class="campaign-hours campaign-hours--link"
                target="_blank"
              >
                {{ item.hours }}
              </router-link>
              <span
                v-else
                class="campaign-hours"
              >{{ item.hours }}</span>
              <span
                v-if="item.locationType"
                class="campaign-location-type"
              >{{ { Z: 'zips', S: 'states', NW: 'nationwide' }[item.locationType] }}</span>
              <span
                v-if="item.budget != null"
                class="campaign-budget"
              >${{ item.budget }} <span
                v-if="item.spendBudgetPercent != null"
                :class="['spend-budget', item.spendBudgetZero ? 'spend-budget--zero' : 'spend-budget--avg', !item.spendBudgetZero && 'spend-budget--clickable']"
                @mouseenter.stop="onSpendEnter($event, item)"
                @mouseleave="spendPopupData = null"
              >{{ item.spendBudgetPercent }}%</span></span>
            </span>
            <span class="campaign-sources">
              <template v-for="(src, si) in visibleSources(idx, item.sources)">
                <span :key="si">{{ si > 0 ? ', ' : '' }}{{ src.name }}</span>
              </template>
              <span
                v-if="hiddenSourceCount(idx, item.sources) > 0"
                class="sources-more sources-more--clickable"
                @click.stop="toggleSourceExpansion(idx)"
              > +{{ hiddenSourceCount(idx, item.sources) }} more</span>
            </span>
          </div>
        </template>
        <template v-else>
          <div class="campaign-line">
            No demand
          </div>
        </template>
      </div>
    </div>
    <SpendBudgetPopup
      v-if="spendPopupData"
      :weekly-spend-data="spendPopupData.weeklySpendData"
      :avg-percent="spendPopupData.avgPercent"
      :style="spendPopupStyle"
    />
  </div>
</template>

<script>
import statesData from '@/data/us-states-svg.json'
import SpendBudgetPopup from '@/components/SpendBudgetPopup.vue'

export default {
  name: 'USStateMap',

  components: {
    SpendBudgetPopup
  },

  props: {
    stateOverrides: {
      type: Array,
      default: () => []
    },
    defaultMargins: {
      type: Object,
      default: () => ({ min: 0, max: 0 })
    },
    stateDemandCounts: {
      type: Object,
      default: null
    },
    showTerritories: {
      type: Boolean,
      default: true
    },
    stateCampaignDetails: {
      type: Object,
      default: null
    },
    selectedDayLabel: {
      type: String,
      default: null
    },
    selectedTimeLabel: {
      type: String,
      default: null
    },
    peakCampaignCount: {
      type: Number,
      default: 0
    }
  },

  data() {
    return {
      hoveredState: null,
      expandedState: null,
      expandedSourceIndices: [],
      pinnedX: 0,
      pinnedY: 0,
      mouseX: 0,
      mouseY: 0,
      insetBoxSize: 40,
      spendPopupData: null,
      spendPopupStyle: {}
    }
  },

  computed: {
    mainStates() {
      return statesData.filter(s => s.position === 'main' && s.path)
    },

    insetStatesWithPath() {
      return statesData.filter(s => s.position === 'inset' && s.path)
    },

    insetStatesWithoutPath() {
      return statesData.filter(s => s.position === 'inset' && !s.path)
    },

    visibleInsetStatesWithoutPath() {
      if (!this.showTerritories) {
        return []
      }

      return this.insetStatesWithoutPath
    },

    useClickPopup() {
      return !!this.stateCampaignDetails
    },

    highlightedState() {
      return this.hoveredState || this.expandedState
    },

    isPinned() {
      return !!this.expandedState
    },

    popupState() {
      return this.isPinned ? this.expandedState : this.hoveredState
    },

    popupStyle() {
      const popupWidth = 350
      const containerWidth = this.$el ? this.$el.clientWidth : 700
      const x = this.isPinned ? this.pinnedX : this.mouseX
      const y = this.isPinned ? this.pinnedY : this.mouseY
      const left = x + 16 + popupWidth > containerWidth
        ? x - popupWidth - 16
        : x + 16
      return {
        left: left + 'px',
        top: (y + 16) + 'px'
      }
    },

    maxDemandCount() {
      if (!this.stateDemandCounts) {
        return 0
      }

      const values = Object.values(this.stateDemandCounts)
      const currentMax = values.length ? Math.max(...values) : 0
      return Math.max(currentMax, this.peakCampaignCount)
    }
  },

  watch: {
    expandedState(val) {
      if (val) {
        document.addEventListener('keydown', this._onKeyDown)
        document.addEventListener('click', this._onClickOutside)
      } else {
        document.removeEventListener('keydown', this._onKeyDown)
        document.removeEventListener('click', this._onClickOutside)
      }

      this.spendPopupData = null
    },
    popupState() {
      this.spendPopupData = null
    }
  },

  created() {
    this._onKeyDown = (e) => {
      if (e.key === 'Escape') {
        this.closePopup()
      }
    }

    this._onClickOutside = (e) => {
      if (!this.$el.contains(e.target)) {
        this.closePopup()
      }
    }
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this._onKeyDown)
    document.removeEventListener('click', this._onClickOutside)
  },

  methods: {
    getStateColor(stateCode) {
      if (this.stateDemandCounts) {
        const count = this.stateDemandCounts[stateCode] || 0
        if (count === 0) {
          return '#e5e7eb'
        }

        const peak = Math.max(this.maxDemandCount, 6)
        const greenMax = Math.max(2, Math.floor(peak / 6))
        const orangeMax = Math.max(5, Math.floor(peak / 2))

        if (count <= greenMax) {
          // Green: light to slightly darker
          const t = greenMax > 1 ? (count - 1) / (greenMax - 1) : 0
          const r = Math.round(187 - t * 55)
          const g = Math.round(247 - t * 20)
          const b = Math.round(208 - t * 40)
          return `rgb(${r}, ${g}, ${b})`
        }

        if (count <= orangeMax) {
          // Orange: light to slightly darker
          const range = orangeMax - greenMax
          const t = range > 1 ? (count - greenMax - 1) / (range - 1) : 0
          const r = Math.round(254 - t * 25)
          const g = Math.round(215 - t * 35)
          const b = Math.round(170 - t * 45)
          return `rgb(${r}, ${g}, ${b})`
        }

        // Red: light to gradually darker
        const range = peak - orangeMax
        const t = range > 1 ? (count - orangeMax - 1) / (range - 1) : 0
        const r = Math.round(254 - t * 25)
        const g = Math.round(202 - t * 40)
        const b = Math.round(202 - t * 40)
        return `rgb(${r}, ${g}, ${b})`
      }

      if (!this.stateOverrides || !Array.isArray(this.stateOverrides)) {
        return '#e5e7eb'
      }

      const override = this.stateOverrides.find(s => s.stateCode === stateCode)
      if (!override) {
        return '#e5e7eb'
      } // Gray: no override

      const avgOverride = (override.minMarginPercent + override.maxMarginPercent) / 2
      const avgDefault = (this.defaultMargins.min + this.defaultMargins.max) / 2
      const diff = avgOverride - avgDefault

      if (diff < -5) {
        return '#4ade80'
      } // Green: lower margins

      if (diff > 5) {
        return '#f87171'
      } // Red: higher margins

      return '#fbbf24' // Yellow: similar margins
    },

    getStateStroke(stateCode) {
      return this.highlightedState === stateCode ? '#1f2937' : '#9ca3af'
    },

    handleStateClick(stateCode) {
      if (this.useClickPopup) {
        if (this.expandedState === stateCode) {
          this.expandedState = null
          this.expandedSourceIndices = []
        } else {
          this.expandedState = stateCode
          this.expandedSourceIndices = []
          this.pinnedX = this.mouseX
          this.pinnedY = this.mouseY
        }
      }

      this.$emit('state-click', stateCode)
    },

    handleStateHover(stateCode) {
      this.hoveredState = stateCode
    },

    onSvgMouseLeave() {
      this.hoveredState = null
      if (!this.expandedState) {
        return
      }
    },

    closePopup() {
      this.expandedState = null
      this.expandedSourceIndices = []
    },

    onMouseMove(event) {
      const rect = this.$el.getBoundingClientRect()
      this.mouseX = event.clientX - rect.left
      this.mouseY = event.clientY - rect.top
    },

    visibleSources(idx, sources) {
      if (this.expandedSourceIndices.includes(idx)) {
        return sources
      }

      return sources.slice(0, 2)
    },

    hiddenSourceCount(idx, sources) {
      if (this.expandedSourceIndices.includes(idx)) {
        return 0
      }

      return Math.max(0, sources.length - 2)
    },

    toggleSourceExpansion(idx) {
      const i = this.expandedSourceIndices.indexOf(idx)
      if (i === -1) {
        this.expandedSourceIndices.push(idx)
      } else {
        this.expandedSourceIndices.splice(i, 1)
      }
    },

    onSpendEnter(event, item) {
      if (item.spendBudgetZero) {
        return
      }

      const rect = event.target.getBoundingClientRect()
      this.spendPopupStyle = {
        position: 'fixed',
        top: (rect.bottom + 4) + 'px',
        left: rect.left + 'px',
        zIndex: 1002
      }
      this.spendPopupData = {
        weeklySpendData: item.weeklySpendData,
        avgPercent: item.spendBudgetPercent
      }
    },

    getStateName(stateCode) {
      const state = statesData.find(s => s.code === stateCode)
      return state ? state.name : stateCode
    },

    getStateMarginText(stateCode) {
      if (this.stateDemandCounts) {
        const count = this.stateDemandCounts[stateCode] || 0
        if (count === 0) {
          return 'No demand'
        }

        return count === 1 ? '1 insight item' : `${count} insight items`
      }

      if (this.stateOverrides && Array.isArray(this.stateOverrides)) {
        const override = this.stateOverrides.find(s => s.stateCode === stateCode)
        if (override) {
          return `Min: ${override.minMarginPercent}% | Max: ${override.maxMarginPercent}%`
        }
      }

      return `Default: Min ${this.defaultMargins.min}% | Max ${this.defaultMargins.max}%`
    }
  }
}
</script>

<style scoped>
.us-state-map {
  position: relative;
  width: 100%;
  max-width: 700px;
}

.state-path {
  cursor: pointer;
  transition: all 0.2s;
}

.state-path:hover {
  opacity: 0.8;
  filter: brightness(1.1);
}

.state-label {
  font-family: Arial, sans-serif;
  text-shadow: 0 0 2px #fff, 0 0 2px #fff, 0 0 2px #fff;
}

.map-tooltip {
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 14px;
  pointer-events: none;
  z-index: 1000;
}

.map-popup {
  position: absolute;
  background: rgba(0, 0, 0, 0.9);
  color: white;
  border-radius: 4px;
  font-size: 14px;
  z-index: 1000;
  max-height: 250px;
  max-width: 440px;
  display: flex;
  flex-direction: column;
  pointer-events: none;
}

.map-popup--pinned {
  pointer-events: auto;
  max-height: 350px;
}

.popup-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  flex-shrink: 0;
}

.popup-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.popup-close {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  padding: 0 0 0 8px;
  flex-shrink: 0;
}

.popup-close:hover {
  color: white;
}

.popup-body {
  padding: 4px 12px 8px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.tooltip-separator {
  width: 1px;
  height: 14px;
  background: rgba(255, 255, 255, 0.35);
}

.tooltip-day {
  color: #93c5fd;
  font-weight: 600;
}

.tooltip-time {
  color: #93c5fd;
  margin-left: -4px;
}

.tooltip-count {
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
}

.campaign-line {
  font-size: 12px;
  padding: 2px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  display: flex;
  gap: 8px;
}

.campaign-line:last-child {
  border-bottom: none;
}

.campaign-meta {
  display: flex;
  gap: 8px;
  white-space: nowrap;
  flex-shrink: 0;
}

.campaign-hours {
  color: #93c5fd;
  white-space: nowrap;
}

.campaign-hours--link {
  pointer-events: auto;
  text-decoration: underline;
  text-decoration-style: dotted;
  cursor: pointer;
}

.campaign-hours--link:hover {
  color: #bfdbfe;
  text-decoration-style: solid;
}

.campaign-budget {
  color: #86efac;
  white-space: nowrap;
}

.spend-budget {
  white-space: nowrap;
  margin-left: 4px;
}

.spend-budget--clickable {
  cursor: pointer;
  pointer-events: auto;
  text-decoration: underline;
  text-decoration-style: dotted;
}

.spend-budget--avg {
  color: #f5deb3;
}

.spend-budget--zero {
  color: #6b7280;
}


.campaign-sources {
  color: #d1d5db;
}

.sources-more {
  color: #93c5fd;
  font-style: italic;
}

.sources-more--clickable {
  cursor: pointer;
  pointer-events: auto;
}

.sources-more--clickable:hover {
  text-decoration: underline;
}

.campaign-location-type {
  color: #9ca3af;
  white-space: nowrap;
}
</style>
